import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import LoadingScreen from './components/LoadingScreen.js';

// Sider
import Login from './pages/Login';
import AgentDashboard from './pages/AgentDashboard';
import ManagementDashboard from './pages/ManagementDashboard';
import TestFirebase from './pages/TestFirebase';
import Race from './pages/Race';
import Calls from './pages/Calls';
import Stats from './pages/Stats';
import Training from './pages/Training';
import Settings from './pages/Settings';

// Komponenter
import NavBar from './components/NavBar';

// Definerer vores tema
const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
    },
    secondary: {
      main: '#f50057',
    },
  },
});

// Beskyttet route komponent
const PrivateRoute = ({ children, userType }: { children: React.ReactNode, userType?: 'agent' | 'management' }) => {
  const { currentUser, loading, userType: authUserType } = useAuth();
  
  if (loading) return null; // Loading håndteres af App-komponenten
  
  if (!currentUser) return <Navigate to="/login" />;
  
  // Hvis en specifik brugertype er krævet, men brugeren ikke har den
  if (userType && authUserType !== userType) {
    // Send agent til agent dashboard
    if (authUserType === 'agent') return <Navigate to="/agent-dashboard" />;
    // Send management til management dashboard
    if (authUserType === 'management') return <Navigate to="/management-dashboard" />;
  }
  
  return <>{children}</>;
};

// Komponent for login-route der omdirigerer brugere, der allerede er logget ind
const LoginRoute = () => {
  const { currentUser, loading, userType } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && currentUser && userType) {
      if (userType === 'agent') {
        navigate('/agent-dashboard');
      } else if (userType === 'management') {
        navigate('/management-dashboard');
      }
    }
  }, [currentUser, loading, userType, navigate]);
  
  if (loading) return null; // Loading håndteres af App-komponenten
  
  return <Login />;
};

// Navigation Bar Wrapper Component
const NavigationBarWrapper = () => {
  const location = useLocation();
  
  // Skip navbar på login siden
  if (location.pathname === '/login') {
    return null;
  }
  
  return <NavBar />;
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Simulerer initial app loading
  useEffect(() => {
    // Skjul loading screen efter en kort forsinkelse
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 sekunder er godt for at sikre logo-animationen ses

    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LoadingScreen isVisible={isLoading} />
      <LanguageProvider>
        <AuthProvider>
          <Router>
            {!isLoading && <NavigationBarWrapper />}
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              
              <Route 
                path="/agent-dashboard" 
                element={
                  <PrivateRoute userType="agent">
                    <AgentDashboard />
                  </PrivateRoute>
                } 
              />
              
              <Route 
                path="/race" 
                element={
                  <PrivateRoute userType="agent">
                    <Race />
                  </PrivateRoute>
                } 
              />
              
              <Route 
                path="/calls" 
                element={
                  <PrivateRoute userType="agent">
                    <Calls />
                  </PrivateRoute>
                } 
              />
              
              <Route 
                path="/stats" 
                element={
                  <PrivateRoute userType="agent">
                    <Stats />
                  </PrivateRoute>
                } 
              />
              
              <Route 
                path="/training" 
                element={
                  <PrivateRoute userType="agent">
                    <Training />
                  </PrivateRoute>
                } 
              />
              
              <Route 
                path="/management-dashboard" 
                element={
                  <PrivateRoute userType="management">
                    <ManagementDashboard />
                  </PrivateRoute>
                } 
              />
              
              <Route 
                path="/settings" 
                element={
                  <PrivateRoute userType="management">
                    <Settings />
                  </PrivateRoute>
                } 
              />
              
              <Route path="/test-firebase" element={<TestFirebase />} />
              
              <Route path="/" element={<Navigate to="/login" />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default App;
